import type { FormEvent, ReactNode } from 'react';
import { useEffect, useId, useRef } from 'react';
import { FIELD_STATUS_OPTIONS, HORSE_STATUS_OPTIONS, type EditorMode } from '../mockData.js';
import { NEW_HORSE_ID, type HorseDraft, type HorseEditorValues, type ReviewHorse } from '../reviewState.js';

export interface HorseEditorDialogProps {
  open: boolean;
  mode: EditorMode;
  horse: ReviewHorse;
  draft: HorseDraft;
  dirty: boolean;
  error: string;
  result: string;
  onClose: () => void;
  onChange: (patch: Partial<HorseEditorValues>) => void;
  onMetadataChange: (patch: Pick<Partial<HorseDraft>, 'effectiveDate' | 'note'>) => void;
  onSave: (returnAfter: boolean) => void;
  onDiscard: () => void;
  /** W32: scope note under the form. Defaults to the design-preview copy; a real session passes the honest local-save note. */
  scopeNote?: string;
}

const PREVIEW_SCOPE_NOTE = 'Design preview only. Saved facts and unsaved drafts stay in this review until reload. No customer record, order or subscription is changed.';

// The parent owns drafts. Native close, Escape and supporting navigation do
// not turn unsaved values into saved facts, or erase the retained draft.
//
// Preservation contract (R8 editor-state scope #21):
//   - Five modes stay distinct: add, profile, status, routine, goals.
//   - The alternate-horse list stays separate from the primary horse,
//     wiring through parent-driven draft state (not inferred here).
//   - Reported age and birth date are INDEPENDENT fields. Entering an
//     age never backfills birth, and entering birth never shortens age.
//     Both route through separate `values.age` / `values.birth` keys
//     and are persisted by `reviewState.applyEditorValues` without
//     cross-derivation.
export function HorseEditorDialog({ open, mode, horse, draft, dirty, error, result,
  onClose, onChange, onMetadataChange, onSave, onDiscard, scopeNote = PREVIEW_SCOPE_NOTE }: HorseEditorDialogProps): ReactNode {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const titleId = useId();
  const values = draft.values;
  const profile = mode === 'profile' || mode === 'add';
  const statusChanged = profile && (horse.id === NEW_HORSE_ID
    ? values.horseStatus !== 'Ownership not confirmed'
    : values.horseStatus !== draft.baseline.horseStatus);

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    if (open && !dialog.open) dialog.showModal();
    if (!open && dialog.open) dialog.close();
  }, [open]);

  const name = values.name || horse.name || 'this horse';
  const title = mode === 'add' ? 'Add a horse' : mode === 'profile' ? `Edit ${name}’s profile`
    : mode === 'routine' ? `${name}’s daily routine` : mode === 'goals' ? `Customer goals for ${name}` : `${name}’s story`;
  const submit = (event: FormEvent<HTMLFormElement>) => { event.preventDefault(); onSave(false); };
  const field = (key: keyof HorseEditorValues, label: string, type = 'text') => (
    <label>{label}<input name={key} type={type} autoComplete="off" value={values[key]}
      onChange={event => onChange({ [key]: event.target.value })} /></label>
  );
  const factFields = (key: 'dose' | 'feed' | 'reason' | 'goal', label: string) => {
    const statusKey = `${key}Status` as 'doseStatus' | 'feedStatus' | 'reasonStatus' | 'goalStatus';
    return <div className="horse-fact-fields" key={key}>
      <label>{label} · Information status<select name={statusKey} value={values[statusKey]}
        onChange={event => onChange({ [statusKey]: event.target.value as HorseEditorValues[typeof statusKey] })}>
        {FIELD_STATUS_OPTIONS.map(option => <option key={option}>{option}</option>)}
      </select></label>
      {field(key, `${label}, as reported`)}
    </div>;
  };

  return <dialog className="horse-dialog" ref={dialogRef} aria-labelledby={titleId}
    onCancel={event => { event.preventDefault(); onClose(); }} onClose={onClose}>
    <header><h2 id={titleId}>{title}</h2><button type="button" aria-label="Close editor" onClick={onClose}>×</button></header>
    <form onSubmit={submit}>
      {profile && <>
        <div className="horse-editor-grid">
          {field('name', 'Horse name')}{field('breed', 'Breed')}
          {field('age', 'Customer-reported age')}{field('birth', 'Birth date, if known', 'date')}
          {field('discipline', 'Discipline')}
          <label>Horse status<select name="horseStatus" value={values.horseStatus}
            onChange={event => onChange({ horseStatus: event.target.value as HorseEditorValues['horseStatus'] })}>
            {HORSE_STATUS_OPTIONS.map(option => <option key={option}>{option}</option>)}
          </select></label>
        </div>
        {statusChanged && <section className="horse-status-change" aria-label="Status change details">
          <p>{draft.baseline.horseStatus} → {values.horseStatus}</p>
          <label>Effective date<input name="effectiveDate" type="date" value={draft.effectiveDate}
            onChange={event => onMetadataChange({ effectiveDate: event.target.value })} /></label>
          <label>Status note · Optional<textarea name="statusNote" value={draft.note}
            onChange={event => onMetadataChange({ note: event.target.value })} /></label>
        </section>}
      </>}
      {(profile || mode === 'story') && <label>Every horse has a story<textarea name="story"
        placeholder={`What would the customer like us to remember about ${name}?`}
        value={values.story} onChange={event => onChange({ story: event.target.value })} /></label>}
      {mode === 'routine' && <>
        <p className="dialog-note">Product: {horse.reportedProduct.name}. Record the customer’s current use, not a dosage recommendation.</p>
        {factFields('dose', 'Daily amount')}{factFields('feed', 'Feeding routine')}
      </>}
      {mode === 'goals' && <>{factFields('reason', 'Why this product')}{factFields('goal', 'Customer goal')}</>}
      {error && <p className="horse-editor-error" role="alert">{error}</p>}
      {result && <p className="horse-editor-result" role="status">{result}</p>}
      <p className="dialog-note">{scopeNote}</p>
      <div className="dialog-actions">
        <button type="button" className="outlined" onClick={onClose}>Close</button>
        <button type="button" className="outlined" onClick={onDiscard} disabled={!dirty}>Discard unsaved changes</button>
        <button type="submit" className="blue-action">Save</button>
        <button type="button" className="blue-action" onClick={() => onSave(true)}>Save &amp; return</button>
      </div>
    </form>
  </dialog>;
}
